import { FileBasic } from '@commenda/types/src/file/types';
import { fetcher, SWRFetcher } from '@commenda/utils';
import useSWR from 'swr';
import {
  FilingPrepaymentResponse,
  FilingTaxBreakdownSummary,
  SalesTaxFiling,
} from './types';

export function useSalesTaxFilingDetail({
  filingId,
  corporationId,
  includeServiceRequest,
  includeAgent,
  includeAssignee,
}: {
  filingId: string;
  corporationId: number;
  includeServiceRequest?: boolean;
  includeAgent?: boolean;
  includeAssignee?: boolean;
}) {
  const queryParams = new URLSearchParams();

  if (includeServiceRequest)
    queryParams.append('includeServiceRequest', 'true');
  if (includeAgent) queryParams.append('includeAgent', 'true');
  if (includeAssignee) queryParams.append('includeAssignee', 'true');
  queryParams.append('corporationId', corporationId.toString());

  const { data, error, isLoading, mutate } = useSWR<{
    data: SalesTaxFiling;
  }>(
    filingId
      ? `new-sales-tax/filings/${filingId}?${queryParams.toString()}`
      : null,
    SWRFetcher(
      {
        method: 'GET',
      },
      undefined
    )
  );

  return {
    filing: data?.data,
    isLoading,
    error,
    mutate,
  };
}

export function useSalesTaxFilingDetailBySalesTaxFilingId({
  salesTaxFilingId,
  corporationId,
  includeServiceRequest,
  includeAgent,
  includeAssignee,
}: {
  salesTaxFilingId: string;
  corporationId: number;
  includeServiceRequest?: boolean;
  includeAgent?: boolean;
  includeAssignee?: boolean;
}) {
  const queryParams = new URLSearchParams();

  if (includeServiceRequest)
    queryParams.append('includeServiceRequest', 'true');
  if (includeAgent) queryParams.append('includeAgent', 'true');
  if (includeAssignee) queryParams.append('includeAssignee', 'true');
  queryParams.append('corporationId', corporationId.toString());

  const { data, error, isLoading, mutate } = useSWR<{
    data: SalesTaxFiling;
  }>(
    salesTaxFilingId
      ? `new-sales-tax/filings/filing-id/${salesTaxFilingId}?${queryParams.toString()}`
      : null,
    SWRFetcher(
      {
        method: 'GET',
      },
      undefined
    )
  );

  return {
    filing: data?.data,
    isLoading,
    error,
    mutate,
  };
}

export function useSalesTaxFilingDocuments(
  filingId: string,
  corporationId: number
) {
  const { data, error, isLoading, mutate } = useSWR<{
    filingConfirmationFile: FileBasic | null;
    paymentConfirmationFile: FileBasic | null;
    message: string;
  }>(
    `new-sales-tax/filings/${filingId}/documents?corporation_id=${corporationId}`,
    SWRFetcher({ method: 'GET' }, undefined)
  );

  return {
    filingConfirmationFile: data?.filingConfirmationFile,
    paymentConfirmationFile: data?.paymentConfirmationFile,
    message: data?.message,
    isLoading,
    error,
    mutate,
  };
}

export function useSalesTaxFilingTransactionSummary(
  filingId: string,
  corporationId: number
) {
  const { data, error, isLoading, mutate } = useSWR<{
    data: FilingTaxBreakdownSummary;
    message: string;
  }>(
    `new-sales-tax/filings/${filingId}/transaction-summary?corporation_id=${corporationId}`,
    SWRFetcher({ method: 'GET' }, undefined)
  );

  return {
    transactionSummary: data?.data,
    message: data?.message,
    isLoading,
    error,
    mutate,
  };
}

export async function refreshSalesTaxFilings(corporationId: number) {
  try {
    const response = await fetcher(`new-sales-tax/filings-sync`, {
      method: 'POST',
      body: { corporation_id: corporationId },
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || 'Failed to approve filing',
      };
    }

    return {
      success: true,
      data: data.data,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
}

export function useFilingPrepaymentList({
  filingId,
  corporationId,
}: {
  filingId: string;
  corporationId: string;
}) {
  const queryParams = new URLSearchParams();

  queryParams.append('corporation_id', corporationId);

  const { data, error, isLoading, mutate } = useSWR<FilingPrepaymentResponse>(
    `new-sales-tax/filings/${filingId}/prepayments?${queryParams.toString()}`,
    SWRFetcher({ method: 'GET' }, undefined)
  );

  return {
    prepayments: data?.data || [],
    isLoading,
    error,
    mutate,
  };
}
